import type { TreeStore } from '@/stores/local/shared/TreeStore';

import type { TreeNodeFile, TreeNodeFolder } from './Tree';
import { AVAILABILITY_REASONS, AvailabilityBlockReason } from './config';

/**
 * Документы: `dsb_status` (pending | completed | failed | error).
 * Папки: при отсутствии dsb — `is_frozen` (агрегат по содержимому).
 */
export function contentAvailableFromApiFields(raw: {
  content_available?: boolean;
  dsb_status?: string | null;
  is_frozen?: boolean;
}): boolean {
  if (typeof raw.content_available === 'boolean') {
    return raw.content_available;
  }

  const dsb = raw.dsb_status?.toLowerCase().trim();

  if (dsb === 'pending' || dsb === 'failed' || dsb === 'error') {
    return false;
  }

  if (dsb === 'completed') {
    return true;
  }

  if (typeof raw.is_frozen === 'boolean') {
    return !raw.is_frozen;
  }

  return true;
}

export function isInsidePersonalWorkspace(store: TreeStore): boolean {
  let current: TreeStore | undefined = store;

  while (current) {
    if (current.data.type === 'folder' && (current.data as TreeNodeFolder).isPersonal) {
      return true;
    }

    current = current.parent;
  }

  return false;
}

/** Иконка «личная папка» (пользователь) только у корневой личной папки, не у вложенных подпапок внутри неё. */
export function isOrganizationRootPersonalFolder(store: TreeStore, model: TreeNodeFolder): boolean {
  if (!model.isPersonal) {
    return false;
  }

  const parent = store.parent;

  if (!parent || parent.data.type !== 'folder') {
    return true;
  }

  return !(parent.data as TreeNodeFolder).isPersonal;
}

export function findTreeStoreByFolderId(root: TreeStore, folderId: string): TreeStore | null {
  const stack: TreeStore[] = [root];

  while (stack.length) {
    const current = stack.pop()!;

    if (current.data.type === 'folder' && current.data.id === folderId) {
      return current;
    }

    if (current.children?.length) {
      stack.push(...current.children);
    }
  }

  return null;
}

export function parseAvailabilityBlockReason(value: string | null | undefined): AvailabilityBlockReason | null {
  if (value && (AVAILABILITY_REASONS as readonly string[]).includes(value)) {
    return value as AvailabilityBlockReason;
  }

  return null;
}

/**
 * Иконка доступности файла — только по `dsb_status`: pending | completed | failed | error.
 */
export function availabilityReasonFromDsbStatus(dsbStatus: string | null | undefined): AvailabilityBlockReason | null {
  if (!dsbStatus) {
    return null;
  }

  const s = dsbStatus.toLowerCase().trim();

  if (s === 'pending') {
    return 'dsb_check';
  }

  if (s === 'failed' || s === 'error') {
    return 'processing_error';
  }

  if (s === 'completed') {
    return null;
  }

  return null;
}

export function getDocumentAvailabilityIconReason(model: TreeNodeFile): AvailabilityBlockReason | null {
  return availabilityReasonFromDsbStatus(model.dsbStatus);
}

export function canDownloadTreeDocument(model: TreeNodeFile): boolean {
  return model.contentAvailable;
}

export function canDownloadDocumentByAvailability(contentAvailable: boolean): boolean {
  return contentAvailable;
}
